import { useAccount } from "@qw/auth";
import {
  AttentionItemType,
  useAttentionFeedChangedSubscription,
  useAttentionFeedQuery,
  useRecentChatSessionsMenuItemsQuery,
} from "@qw/graphql";
import { useMemo } from "react";

/** Rows the Alby menu shows. Both source lists are server-capped well above this. */
export const ALBY_MENU_ITEM_LIMIT = 10;

/**
 * How recent an open ask must be to mark its chat. The attention feed is
 * unwindowed and mostly stale — of ~2,004 open asks, 96.9% are older than three
 * days — so an unfiltered merge would pin months-old rows to the top of a menu
 * whose job is "what am I working on". Seven days keeps the marked set inside
 * the feed's own 100-row cap (102 open asks company-wide fall inside it, at most
 * 23 for any one broker), which is why this needs no new server query.
 */
export const ALBY_MENU_ALERT_WINDOW_DAYS = 7;

const ALERT_WINDOW_MS = ALBY_MENU_ALERT_WINDOW_DAYS * 24 * 60 * 60 * 1000;

/**
 * An `attentionFeed` row, narrowed to the fields the merge reads. The anchors are
 * already resolved against the ask's **root foreground chat** server-side
 * (`resolveEffectiveAnchorMetadata`), which is what makes the two lists mergeable:
 * an alert arrives describing a chat that `recentChatSessionsMenuItems` could
 * also return.
 */
export interface AlbyAlertSource {
  id: string;
  itemType: AttentionItemType;
  displayTitle: string;
  occurredAt: string;
  insuredId?: string | null;
  inboxConversationId?: string | null;
  rootChatSessionId?: string | null;
}

/** A `recentChatSessionsMenuItems` row, narrowed to the fields the merge reads. */
export interface AlbyRecentChatSource {
  id: string;
  displayTitle: string;
  insuredId?: string | null;
  inboxConversationId?: string | null;
  lastMessageAt?: string | null;
}

export interface AlbyMenuItem {
  /** The anchor both lists agreed on — stable across renders, usable as a React key. */
  key: string;
  displayTitle: string;
  insuredId: string | null;
  inboxConversationId: string | null;
  /** The foreground chat's own id, set when this row appeared in the recent list. */
  chatSessionId: string | null;
  /** Book-chat root session id, set when an ask anchored to one. */
  rootChatSessionId: string | null;
  /** True when at least one open ask inside the window belongs to this chat. */
  hasAlert: boolean;
  /**
   * `attention_item` row ids folded into this row — the dismiss targets, not
   * session ids. Nothing renders a dismiss control today: B-2048 retired the
   * attention dropdown that had one, on the grounds that the menu's seven-day
   * marker window ages a stale ask out without the broker clearing it. Kept
   * because it is what any future dismiss affordance would act on.
   */
  attentionItemIds: string[];
  /** What the row is ranked and dated by: the newest ask for an alerted row, the last message otherwise. */
  activityAt: string | null;
}

interface AnchorSource {
  insuredId?: string | null;
  inboxConversationId?: string | null;
  sessionId?: string | null;
}

/**
 * The identity a chat row is folded on. Conversation first, then insured, then
 * the chat session itself — the same precedence both lists use to build their
 * display titles, so two rows sharing an anchor really are the same chat.
 *
 * Returns `null` when nothing resolved. For an alert that means
 * `buildSessionAnchorView` deliberately emitted `null` anchors rather than
 * deep-link the broker to a 403, leaving no chat to mark.
 */
function anchorKeyFor({ insuredId, inboxConversationId, sessionId }: AnchorSource): string | null {
  if (inboxConversationId) {
    return `conversation:${inboxConversationId}`;
  }
  if (insuredId) {
    return `insured:${insuredId}`;
  }
  if (sessionId) {
    return `session:${sessionId}`;
  }
  return null;
}

function timestampOf(value: string | null): number {
  if (!value) {
    return Number.NEGATIVE_INFINITY;
  }
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}

interface BuildAlbyMenuItemsArgs {
  alerts: AlbyAlertSource[];
  recentChats: AlbyRecentChatSource[];
  /** Window origin. Injected so tests can pin it; defaults to now. */
  now?: Date;
  /**
   * Rows the host app can actually navigate to. Applied before the cap, so an app
   * that reaches only some anchors — web has insured pages but no inbox — still
   * gets a full menu of the chats it can open rather than whatever survived the
   * top ten. Omitted means every row is reachable.
   */
  isReachable?: (item: AlbyMenuItem) => boolean;
}

/**
 * Merges the open-ask feed into the recent-chat list, one row per chat.
 *
 * Ranking is alerts-first, then recency inside each group, each group dated by
 * its own signal — the ask's `occurredAt` for an alerted row, `lastMessageAt`
 * otherwise. A chat's two timestamps diverge in both directions, so the
 * alternative (one blended key taking whichever is newer) reads as a single list
 * but lets an alert sort below chatter. Swapping to it later is a one-line change
 * to `activityAt` plus dropping the `hasAlert` tier.
 */
export function buildAlbyMenuItems({
  alerts,
  recentChats,
  now = new Date(),
  isReachable,
}: BuildAlbyMenuItemsArgs): AlbyMenuItem[] {
  const windowStart = now.getTime() - ALERT_WINDOW_MS;
  const rows = new Map<string, AlbyMenuItem>();

  for (const alert of alerts) {
    if (alert.itemType !== AttentionItemType.AwaitingInput) {
      continue;
    }
    if (timestampOf(alert.occurredAt) < windowStart) {
      continue;
    }

    const key = anchorKeyFor({
      insuredId: alert.insuredId,
      inboxConversationId: alert.inboxConversationId,
      sessionId: alert.rootChatSessionId,
    });
    if (!key) {
      continue;
    }

    const existing = rows.get(key);
    if (existing) {
      // A second open ask on the same chat marks the row it already has. Date the
      // row by the newest of them rather than by whichever arrived first — the
      // resolver does order the feed `occurredAt desc`, but that contract lives in
      // another file and nothing here would notice it changing.
      existing.attentionItemIds.push(alert.id);
      if (timestampOf(alert.occurredAt) > timestampOf(existing.activityAt)) {
        existing.activityAt = alert.occurredAt;
      }
      continue;
    }

    rows.set(key, {
      key,
      displayTitle: alert.displayTitle,
      insuredId: alert.insuredId ?? null,
      inboxConversationId: alert.inboxConversationId ?? null,
      chatSessionId: null,
      rootChatSessionId: alert.rootChatSessionId ?? null,
      hasAlert: true,
      attentionItemIds: [alert.id],
      activityAt: alert.occurredAt,
    });
  }

  for (const chat of recentChats) {
    const key = anchorKeyFor({
      insuredId: chat.insuredId,
      inboxConversationId: chat.inboxConversationId,
      sessionId: chat.id,
    });
    if (!key) {
      continue;
    }

    const existing = rows.get(key);
    if (existing) {
      // Only fill a row an *alert* opened. The recent list is the chat itself, so it
      // carries the session id the alert couldn't (an ask's own `chatSessionId` is
      // the execution session, not the chat the broker opens) and the title this
      // menu shows on every other row. A row that already holds a session id was
      // opened by an earlier recent chat, and the list arrives `lastMessageAt desc`
      // — so this second chat on the same anchor is the older one, and overwriting
      // would point the row's navigation and unread lookup at a stale session.
      if (!existing.chatSessionId) {
        existing.chatSessionId = chat.id;
        existing.displayTitle = chat.displayTitle;
      }
      continue;
    }

    rows.set(key, {
      key,
      displayTitle: chat.displayTitle,
      insuredId: chat.insuredId ?? null,
      inboxConversationId: chat.inboxConversationId ?? null,
      chatSessionId: chat.id,
      rootChatSessionId: null,
      hasAlert: false,
      attentionItemIds: [],
      activityAt: chat.lastMessageAt ?? null,
    });
  }

  return [...rows.values()]
    .filter((item) => isReachable?.(item) ?? true)
    .sort((a, b) => {
      if (a.hasAlert !== b.hasAlert) {
        return a.hasAlert ? -1 : 1;
      }
      return timestampOf(b.activityAt) - timestampOf(a.activityAt);
    })
    .slice(0, ALBY_MENU_ITEM_LIMIT);
}

export interface UseAlbyMenuItemsResult {
  items: AlbyMenuItem[];
  loading: boolean;
}

/**
 * The Alby menu's list: recent foreground chats with the ones holding an open ask
 * marked and lifted to the top. Both queries are the existing ones — the merge is
 * client-side because an alert is already a property of a chat the recent list can
 * return, so no server-side joined query is needed.
 */
export function useAlbyMenuItems({
  skip = false,
  isReachable,
}: {
  skip?: boolean;
  isReachable?: (item: AlbyMenuItem) => boolean;
} = {}): UseAlbyMenuItemsResult {
  const { user } = useAccount();
  const currentUserId = user?.id ?? "";

  const attention = useAttentionFeedQuery({ skip, fetchPolicy: "cache-and-network" });
  const recent = useRecentChatSessionsMenuItemsQuery({ skip });

  // The marker has to keep up with the feed it reflects. This signal fires exactly
  // when a row enters or leaves this broker's feed, so a chat that just started
  // waiting gets marked without reopening the menu; the Query stays the source of
  // truth, so a dropped event self-heals on the next one.
  useAttentionFeedChangedSubscription({
    variables: { userId: currentUserId },
    skip: skip || !currentUserId,
    onData: () => {
      void attention.refetch();
    },
  });

  const alerts = attention.data?.attentionFeed.items;
  const recentChats = recent.data?.recentChatSessionsMenuItems;

  const items = useMemo(
    () => buildAlbyMenuItems({ alerts: alerts ?? [], recentChats: recentChats ?? [], isReachable }),
    [alerts, recentChats, isReachable]
  );

  // Only the first response counts as loading. `cache-and-network` keeps
  // `loading` true through every background refetch, and flipping the menu back
  // to a spinner because a poll is in flight would hide a list we already have.
  const loading = (attention.loading && !alerts) || (recent.loading && !recentChats);

  return { items, loading };
}
